<template>
    <div
        ref="container"
        class="ScrollableContainer"
        :class="{ 'is-scrollable': showGradient }"
        :style="{ '--max-height': maxHeight }"
        @scroll="handleScroll"
    >
        <div class="ScrollableContainer-content">
            <div v-if="items && items.length > 0">
                <div
                    v-for="(item, index) in items"
                    :key="getItemKey(item, index)"
                    class="item-wrapper"
                    :style="{ 'contain-intrinsic-size': `${itemHeight}px` }"
                >
                    <slot :item="item" :index="index" />
                </div>
            </div>
            <div v-if="!items || items.length === 0" class="empty-list">
                <slot />
            </div>
        </div>
        <div
            v-if="showGradient"
            class="ScrollableContainer-gradient"
            :style="{ opacity: gradientOpacity }"
        ></div>
    </div>
</template>

<script>
export default {
    name: 'ScrollableContainer',
    props: {
        maxHeight: {
            type: String,
            default: '300px',
        },
        items: {
            type: Array,
            default: () => [],
        },
        itemHeight: {
            type: Number,
            default: 60,
        },
        buffer: {
            type: Number,
            default: 5,
        },
        keyField: {
            type: String,
            default: 'id',
        },
    },
    data() {
        return {
            showGradient: false,
            gradientOpacity: 1,
            contentWatcher: null,
            lastScrollHeight: 0,
            lastClientHeight: 0,
        };
    },
    computed: {},
    mounted() {
        setTimeout(() => {
            this.checkScrollable();
            this.updateGradient();

            const container = this.$refs.container;
            if (container) {
                this.lastScrollHeight = container.scrollHeight;
                this.lastClientHeight = container.clientHeight;
            }
        }, 100);

        this.setupContentWatcher();
    },

    beforeDestroy() {
        if (this.contentWatcher) {
            clearInterval(this.contentWatcher);
        }
    },
    methods: {
        getItemKey(item, index) {
            return item[this.keyField] || index;
        },

        checkScrollable() {
            const container = this.$refs.container;
            if (!container) return;

            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;
            const hasOverflow = scrollHeight > clientHeight;

            this.showGradient = hasOverflow;
        },

        updateGradient() {
            const container = this.$refs.container;
            if (!container) return;

            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;

            if (scrollHeight <= clientHeight) {
                this.gradientOpacity = 0;
                return;
            }

            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            const gradientHeight = 20;

            if (distanceFromBottom <= gradientHeight) {
                this.gradientOpacity = Math.max(0, distanceFromBottom / gradientHeight);
            } else {
                this.gradientOpacity = 1;
            }
        },

        handleScroll() {
            this.updateGradient();
        },

        recheckScrollability() {
            this.$nextTick(() => {
                this.checkScrollable();
                this.updateGradient();
            });
        },

        setupContentWatcher() {
            this.contentWatcher = setInterval(() => {
                const container = this.$refs.container;
                if (container) {
                    const currentScrollHeight = container.scrollHeight;
                    const currentClientHeight = container.clientHeight;

                    if (
                        currentScrollHeight !== this.lastScrollHeight ||
                        currentClientHeight !== this.lastClientHeight
                    ) {
                        this.lastScrollHeight = currentScrollHeight;
                        this.lastClientHeight = currentClientHeight;
                        this.checkScrollable();
                        this.updateGradient();
                    }
                }
            }, 500);
        },
    },
};
</script>

<style lang="scss" scoped>
.ScrollableContainer {
    max-height: var(--max-height, 300px);
    overflow-y: auto;

    position: relative;

    &-content {
    }

    .item-wrapper {
        content-visibility: auto;
    }

    &-gradient {
        position: absolute;
        bottom: 0px;
        left: 0;
        right: 0;
        height: 40px;
        background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.8) 50%,
            rgba(255, 255, 255, 1) 100%
        );
        pointer-events: none;
        transition: opacity 0.2s ease;
    }

    &.is-scrollable {
    }
}
</style>
